import type { EditingRange } from '@/version-types';

export class RangeHelper {
    private constructor() {
        throw new Error('RangeHelper should not be instanced');
    }

    /**
     * 获取 rangeContainer 在 childNodes 中的位置信息
     * @param rangeContainer - range.startContainer 或 range.endContainer
     * @param childNodes - 可编辑区域根元素的 children
     * @returns node - 元素节点
     *          index - children 中的索引
     */
    private static getRangeContainerInfo(rangeContainer: Node, children: HTMLCollection) {
        const node = rangeContainer.nodeType === Node.TEXT_NODE ? (rangeContainer.parentNode as Node) : rangeContainer;

        const index = Array.prototype.indexOf.call(children, node);

        return {
            node,
            index,
        };
    }

    /**
     * 修正 range 的 offset
     *
     * - 部分浏览器下（比如火狐），会将换行符直接渲染成 <br> 标签，导致 offset 有差异
     * - 在本编辑器内，如果 span 除文本节点外还包含 element 节点
     * - 此时的 offset 就代表节点偏移，而不是文本偏移，故需要统一成文本偏移
     *
     * @param startNode - 选区的起始节点
     * @param endNode - 选区的结束节点
     * @param range - native Range
     * @returns startOffset - 修正后的起始 offset
     *          endOffset - 修正后的结束 offset
     */
    private static normalizeOffset(startNode: Node, endNode: Node, range: Range) {
        const childNodesOfStart = startNode.childNodes;
        const childNodesOfEnd = endNode.childNodes;

        let { startOffset, endOffset } = range;

        if (childNodesOfStart.length > 1) {
            // contains element node
            startOffset += Array.from(childNodesOfStart)
                .slice(0, startOffset)
                .reduce((acc, node) => {
                    return acc + (node.nodeType === Node.ELEMENT_NODE ? 1 : node.textContent?.length || 0);
                }, 0);
        }

        if (childNodesOfEnd.length > 1) {
            // contains element node
            endOffset += Array.from(childNodesOfEnd)
                .slice(0, endOffset)
                .reduce((acc, node) => {
                    return acc + (node.nodeType === Node.ELEMENT_NODE ? 1 : node.textContent?.length || 0);
                }, 0);
        }

        return {
            startOffset,
            endOffset,
        };
    }

    /**
     * 获取选中区域状态
     *
     * @param root - 根节点
     * @returns 选中区域状态
     */
    static getEditingRange(root: HTMLElement): EditingRange | null {
        const selection = window.getSelection();
        if (!selection || selection.rangeCount === 0) return null;

        const children = root.children;
        if (children.length === 0) return null;

        const range = selection.getRangeAt(0);

        const { node: startNode, index: startIndex } = this.getRangeContainerInfo(range.startContainer, children);
        const { node: endNode, index: endIndex } = this.getRangeContainerInfo(range.endContainer, children);
        const { startOffset, endOffset } = this.normalizeOffset(startNode, endNode, range);

        const isCollapsed = range.collapsed;
        const isEnd = children[children.length - 1] === endNode && endOffset === endNode.textContent?.length;

        return {
            startIndex,
            endIndex,
            startOffset,
            endOffset,
            isCollapsed,
            isEnd,
        };
    }

    /**
     * 将 EditingRange 应用到文档中
     *
     * @param root - 根节点
     * @param range - 编辑范围
     */
    static applyEditingRange(root: HTMLElement, editingRange: EditingRange) {
        const childNodes = root.childNodes;
        if (childNodes.length === 0) return;

        const { startIndex, endIndex, startOffset, endOffset } = editingRange;
        const startNode = childNodes[startIndex];
        const endNode = childNodes[endIndex];

        if (!startNode || !endNode) return;

        const range = document.createRange();

        const firstChild = startNode.firstChild;
        if (firstChild) {
            range.setStart(firstChild, Math.min(startOffset, firstChild.textContent?.length || 0));
        }

        const lastChild = endNode.lastChild;
        if (lastChild) {
            range.setEnd(lastChild, Math.min(endOffset, lastChild.textContent?.length || 0));
        }

        const selection = window.getSelection();
        if (selection) {
            selection.removeAllRanges();
            selection.addRange(range);
        }
    }

    /**
     * 选中所有文本
     * // TODO: 可以用 selection.selectAllChildren API 进行优化
     * @param root - 根节点元素
     */
    static selectAll(root: HTMLElement) {
        const children = root.children;
        if (children.length === 0) return null;

        const selection = window.getSelection();
        if (!selection) return null;

        const range = document.createRange();
        range.selectNodeContents(root);

        // --- set start ---
        const firstChild = root.firstChild!;
        const firstChildChildNodes = firstChild.childNodes;
        const startNode = (() => {
            if (firstChildChildNodes.length === 0) return firstChild;
            return firstChildChildNodes[0];
        })();
        const startOffset = 0;

        range.setStart(startNode, startOffset);

        // --- set end ---
        const lastChild = root.lastChild!;
        const lastChildChildNodes = lastChild.childNodes;
        const endNode = (() => {
            if (lastChildChildNodes.length === 0) return lastChild;
            return lastChildChildNodes[lastChildChildNodes.length - 1];
        })();
        const endOffset = (() => {
            if (lastChildChildNodes.length === 0) return 0;
            return Array.from(lastChildChildNodes).reduce((acc, node) => {
                return acc + (node.nodeType === Node.ELEMENT_NODE ? 1 : node.textContent?.length || 0);
            }, 0);
        })();

        range.setEnd(endNode, endOffset);

        selection.removeAllRanges();
        selection.addRange(range);

        const editingRange: EditingRange = {
            startIndex: 0,
            endIndex: Array.prototype.indexOf.call(children, lastChild),
            startOffset,
            endOffset,
            isCollapsed: false,
            isEnd: true,
        };

        return editingRange;
    }
}
